import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import './Chatbot.scss';
import { Modal } from '../../modal/Modal';
import { useDispatch, useSelector } from 'react-redux';
import { setShowChatBot, setShowConfirmationPopupToCloseBot, setChatBotErrorMsg, clearChatBotProperty } from '../../../features/chat/chatbotSlice';
import { fetchAndInitChatbotNew } from '../../../features/chatbotNew/chatbotNewThunks';
import ChatbotHeader from './ChatbotHeader';
import NewChatbotContainer from './NewChatbotContainer';
import AlertModal from '../AlertModal';
import useNewChatbotBody from './hooks/useNewChatbotBody';
import routes from '../../../constants/Routes';

const NewChatbot = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const { handleCloseChatbotNew } = useNewChatbotBody();

    const chatBotUIState = useSelector(state => state.chatBot?.chatBotUIState || {});
    const chatBotNewState = useSelector(state => state.chatBotNew);

    const showChatBot = chatBotUIState?.showChatBot || false;
    const initChatBot = chatBotUIState?.initChatBot || false;
    const showConfirmationPopupToCloseBot = chatBotUIState?.showConfirmationPopupToCloseBot || false;
    const chatBotErrorMsg = chatBotUIState?.chatBotErrorMsg || '';
    const newChatbotError = chatBotNewState?.error || '';

    const hasInitializedRef = React.useRef(false);
    const isClosedRef = React.useRef(false);
    const isExplicitStartRef = React.useRef(false); // Tracks if this component mount was from explicit Start button

    // Combined effect for both initChatBot changes and location changes
    useEffect(() => {
        const isDashboardPage = location.pathname === routes.dashboard;

        // Don't initialize if chatbot was explicitly closed
        if (isClosedRef.current) {
            return;
        }

        // Detect if this is an explicit start (initChatBot is true but showChatBot is false)
        // This means startChatBot() was just called, setting initChatBot=true but showChatBot hasn't been set yet
        const isExplicitStart = initChatBot && !showChatBot;

        if (isExplicitStart) {
            isExplicitStartRef.current = true;
            dispatch(setShowChatBot(true));
            dispatch(fetchAndInitChatbotNew());
            hasInitializedRef.current = true;
            return;
        }

        // Don't auto-initialize on dashboard page
        if (isDashboardPage) {
            // Close chatbot if it's open from previous session or navigation
            // But NOT if this was an explicit start (isExplicitStartRef would be true)
            if ((showChatBot || initChatBot) && !isExplicitStartRef.current) {
                dispatch(setShowChatBot(false));
                dispatch(clearChatBotProperty());
                hasInitializedRef.current = false;
            }
            return;
        }

        // On non-dashboard pages, allow chatbot if initChatBot is true and not yet initialized
        if (initChatBot && !hasInitializedRef.current) {
            dispatch(setShowChatBot(true));
            dispatch(fetchAndInitChatbotNew());
            hasInitializedRef.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, initChatBot, showChatBot]);

    useEffect(() => {
        // Track when chatbot is explicitly closed (not just navigated away)
        if (showChatBot === false && initChatBot) {
            isClosedRef.current = true;
        }
        // Reset when chatbot opens again
        if (showChatBot === true && initChatBot) {
            isClosedRef.current = false;
        }
    }, [showChatBot, initChatBot]);

    const toggleChatModal = () => {
        dispatch(setShowConfirmationPopupToCloseBot(true));
    };

    const onToggleConfirmClose = () => {
        dispatch(setShowConfirmationPopupToCloseBot(!showConfirmationPopupToCloseBot));
    };

    const onConfirmClose = () => {
        handleCloseChatbotNew();
    };

    const onClickCloseErrorAlert = () => {
        dispatch(setChatBotErrorMsg(''));
    };

    const errorMsg = chatBotErrorMsg || newChatbotError;

    return (
        <Modal
            isOpen={showChatBot}
            toggle={toggleChatModal}
            backdrop={false}
            className="mmh-modal-xxl width-full mmh-modal-reset"
            modalClassName="mmh-chat-modal modal-fix chat-bot"
        >
            <AlertModal
                show={!!errorMsg}
                title="Error"
                type="error"
                onClose={onClickCloseErrorAlert}
                onConfirm={onClickCloseErrorAlert}
                confirmBtnText="OK"
                showCancel={false}
            >
                {errorMsg}
            </AlertModal>

            {showConfirmationPopupToCloseBot && (
                <AlertModal
                    show={showConfirmationPopupToCloseBot}
                    title="Confirmation"
                    type="warning"
                    onClose={onToggleConfirmClose}
                    onCancel={onToggleConfirmClose}
                    onConfirm={onConfirmClose}
                    confirmBtnText="Yes"
                    cancelBtnText="No"
                    showCancel={true}
                    showCloseButton={true}
                >
                    Are you sure you want to close?
                </AlertModal>
            )}

            <div className="mmh-chat-container height-100vh mmh-chat">
                <div className="mmh-portlet mmh-portlet--last bg-color-white-dark">
                    <ChatbotHeader toggleChatModal={toggleChatModal} />
                    <NewChatbotContainer />
                </div>
            </div>
        </Modal>
    );
};

export default NewChatbot;
